import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { type Database } from '@/integrations/supabase/types';
import { type Question, type NewQuestion } from '@/components/admin/questions/types';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { User } from '@supabase/supabase-js';
import Papa from 'papaparse';
import { z } from 'zod';
import { validateUploadFile } from '@/lib/validation';

// Validation schema for imported questions
const questionImportSchema = z.object({
  id: z.string().optional(),
  question: z.string().optional(),
  question_text: z.string().optional(),
  question_type: z.string().optional(),
  options: z.array(z.string()).optional(),
  choices: z.array(z.string()).optional(),
  correct_options: z.array(z.string()).optional(),
  correct_answer: z.union([z.string(), z.array(z.string())]).optional(),
  explanation: z.string().optional(),
  answer: z.string().optional(),
});

// This interface handles various possible field names from imported JSON files.
interface RawQuestionData {
  id?: string;
  question?: string;
  question_text?: string;
  question_type?: string;
  options?: string[];
  choices?: string[];
  correct_options?: string[];
  correct_answer?: string | string[];
  explanation?: string;
  answer?: string;
}

// Helper to parse question data from Supabase, ensuring arrays are correctly typed.
const parseSupabaseQuestion = (q: Database['public']['Tables']['questions']['Row']): Question => ({
  ...q,
  options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options || [],
  correct_answer: typeof q.correct_answer === 'string' ? JSON.parse(q.correct_answer) : q.correct_answer || [],
});

export const useQuestions = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error fetching user:', error);
        toast.error('Could not fetch user session.');
      } else {
        setUser(data.user);
      }
    };
    fetchUser();
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      // Supabase defaults to 1000 rows max, so we need to fetch in batches
      const allQuestions: Database['public']['Tables']['questions']['Row'][] = [];
      const batchSize = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('questions')
          .select('id, custom_id, question_text, options, correct_answer, explanation, category_id, is_active, university, question_images, explanation_images, question_type, created_at')
          .order('created_at', { ascending: false })
          .range(from, from + batchSize - 1);

        if (error) {
          console.error('Error fetching questions:', error);
          toast.error('Failed to fetch questions.');
          return;
        }

        if (data) {
          allQuestions.push(...data);
          // If we got fewer than batchSize, we've reached the end
          hasMore = data.length === batchSize;
          from += batchSize;
        } else {
          hasMore = false;
        }
      }

      const parsedQuestions = allQuestions.map(parseSupabaseQuestion);
      setQuestions(parsedQuestions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to fetch questions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const filteredQuestions = useMemo(() => {
    return questions
      .filter(q => {
        const searchLower = searchTerm.toLowerCase();
        return q.question_text.toLowerCase().includes(searchLower) ||
               (q.custom_id && q.custom_id.toLowerCase().includes(searchLower)) ||
               q.id.toLowerCase().includes(searchLower);
      })
      .filter(q => typeFilter === 'all' || q.question_type === typeFilter);
  }, [questions, searchTerm, typeFilter]);

  const paginatedQuestions = useMemo(() => {
    const startIndex = (currentPage - 1) * questionsPerPage;
    return filteredQuestions.slice(startIndex, startIndex + questionsPerPage);
  }, [filteredQuestions, currentPage, questionsPerPage]);

  const addQuestion = async (question: NewQuestion) => {
    if (!user) {
      toast.error('You must be logged in to add a question.');
      return;
    }

    const questionWithUser = { 
      ...question, 
      created_by: user.id,
      options: JSON.stringify(question.options),
      correct_answer: JSON.stringify(question.correct_answer),
    };

    const { data, error } = await supabase
      .from('questions')
      .insert([questionWithUser])
      .select();

    if (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question.');
    } else if (data) {
      const newQuestion = parseSupabaseQuestion(data[0]);
      setQuestions(prev => [newQuestion, ...prev]);
      toast.success('Question added successfully.');
    }
  };

  const updateQuestion = async (id: string, updates: Partial<NewQuestion>) => {
    const { options, correct_answer, ...rest } = updates;

    // Create a type-safe object for the Supabase update payload.
    const updateData: Omit<Partial<Question>, 'options' | 'correct_answer'> & {
      options?: string;
      correct_answer?: string;
    } = { ...rest };

    if (options) {
      updateData.options = JSON.stringify(options);
    }
    if (correct_answer) {
      updateData.correct_answer = JSON.stringify(correct_answer);
    }

    const { data, error } = await supabase
      .from('questions')
      .update(updateData)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question.');
    } else if (data) {
      const updatedQuestion = parseSupabaseQuestion(data[0]);
      setQuestions(prev => prev.map(q => (q.id === id ? updatedQuestion : q)));
      toast.success('Question updated successfully.');
    }
  };

  const deleteQuestion = async (id: string) => {
    const { error } = await supabase.from('questions').delete().eq('id', id);

    if (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question.');
    } else {
      setQuestions(prev => prev.filter(q => q.id !== id));
      toast.success('Question deleted successfully.');
    }
  };

  const bulkUpdateQuestions = async (ids: string[], updates: Partial<NewQuestion>) => {
    if (!user) {
      toast.error('You must be logged in to update questions.');
      return;
    }

    const { options, correct_answer, ...rest } = updates;

    const updateData: Omit<Partial<Question>, 'options' | 'correct_answer'> & {
      options?: string;
      correct_answer?: string;
    } = { ...rest };

    if (options) {
      updateData.options = JSON.stringify(options);
    }
    if (correct_answer) {
      updateData.correct_answer = JSON.stringify(correct_answer);
    }

    const { data, error } = await supabase
      .from('questions')
      .update(updateData)
      .in('id', ids)
      .select();

    if (error) {
      console.error('Error bulk updating questions:', error);
      toast.error('Failed to update questions.');
    } else if (data) {
      const updatedQuestions = data.map(parseSupabaseQuestion);
      setQuestions(prev =>
        prev.map(q => updatedQuestions.find(uq => uq.id === q.id) || q)
      );
      toast.success(`${data.length} questions updated successfully.`);
    }
  };

  const bulkDeleteQuestions = async (ids: string[]) => {
    const { error } = await supabase.from('questions').delete().in('id', ids);
    if (error) {
      toast.error('Failed to delete questions.');
    } else {
      setQuestions(prev => prev.filter(q => !ids.includes(q.id)));
      toast.success(`${ids.length} questions deleted.`);
    }
  };

  const bulkInsertQuestions = async (questionsToInsert: NewQuestion[]) => {
    if (!user) {
      toast.error('You must be logged in to import questions.');
      return;
    }

    try {
      // Transform the question to include the custom_id and format arrays as JSON strings
      const formattedQuestions = questionsToInsert.map(question => {
        const { id: custom_id, ...rest } = question;
        return {
          ...rest,
          custom_id, // This is the id from the uploaded file
          created_by: user.id,
          options: Array.isArray(question.options) ? JSON.stringify(question.options) : question.options,
          correct_answer: Array.isArray(question.correct_answer) ? JSON.stringify(question.correct_answer) : question.correct_answer,
        };
      });

    const { data, error } = await supabase
    .from('questions')
    .insert(formattedQuestions)
    .select();

    if (error) {
      console.error(`Error to inserting questions: ${error.message}`);
      throw new Error(`Failed to insert questions: ${error.message}`);
    }

    // Parse the response to ensure array are correctly formatted
    const parsedQuestions = data.map(parseSupabaseQuestion);
    setQuestions(prev => [...prev, ...parsedQuestions]);
    toast.success(`${data.length} questions added successfully.`);
    return data;
    } catch (error) {
      console.error('Error bulkInsertQuestions:', error);
      toast.error('Failed to insert questions. Please try again.');
      throw error;
    }
  };

  const uploadFile = async (file: File, categoryId?: string, university?: string) => {
    setUploading(true);
    try {
      if (!user) {
        throw new Error('User must be logged in to upload questions.');
      }

      // Validate file before processing
      const fileValidation = validateUploadFile(file);
      if (!fileValidation.valid) {
        throw new Error(fileValidation.error);
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      let parsedQuestions: RawQuestionData[] = [];

      if (fileExtension === 'json') {
        const fileContent = await file.text();
        const jsonData = JSON.parse(fileContent);

        // Validate JSON structure
        const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
        const validationResults = dataArray.map((item, index) => {
          const result = questionImportSchema.safeParse(item);
          if (!result.success) {
            console.warn(`Question at index ${index} failed validation:`, result.error.issues);
          }
          return result;
        });

        // Filter only valid questions
        parsedQuestions = validationResults
          .filter(r => r.success)
          .map(r => r.data as RawQuestionData);

        const invalidCount = validationResults.filter(r => !r.success).length;
        if (invalidCount > 0) {
          toast.warning(`${invalidCount} question(s) skipped due to invalid format.`);
        }
      } else if (fileExtension === 'csv') {
        const csvText = await file.text();
        const result = Papa.parse<Record<string, string>>(csvText, {
          header: true,
          skipEmptyLines: true,
        });
        
        parsedQuestions = result.data.map(row => {
          // Map CSV columns to question fields
          const question: RawQuestionData = {
            question_text: row.question || row.question_text || '',
            question_type: row.question_type || 'multiple_choice',
            options: [],
            correct_answer: [],
            explanation: row.explanation || ''
          };

          // Parse options (assuming they're in columns like option1, option2, etc.)
          const options = [];
          for (let i = 1; i <= 10; i++) {
            const option = row[`option${i}`] || row[`choice${i}`];
            if (option) options.push(option);
          }
          question.options = options;

          // Parse correct answers (assuming they're in a column like correct_answer or correct_options)
          if (row.correct_answer) {
            question.correct_answer = row.correct_answer.split(',').map(s => s.trim());
          } else if (row.correct_options) {
            question.correct_options = row.correct_options.split(',').map(s => s.trim());
          }

          return question;
        });
      } else {
        throw new Error('Unsupported file format. Please upload a CSV or JSON file.');
      }

      const getCorrectAnswers = (q: RawQuestionData): string[] => {
        if (q.correct_options && Array.isArray(q.correct_options)) return q.correct_options;
        if (q.correct_answer) {
          return Array.isArray(q.correct_answer) ? q.correct_answer : [q.correct_answer];
        }
        if (q.answer) return [q.answer];
        return [];
      };

      const newQuestions: NewQuestion[] = parsedQuestions.map(q => ({
        id: q.id || uuidv4(),
        question_text: q.question || q.question_text || '',
        question_type: q.question_type || 'multiple_choice',
        options: q.options || q.choices || [],
        correct_answer: getCorrectAnswers(q),
        explanation: q.explanation || '',
        category_id: categoryId,
        created_by: user.id,
        university: university,
      }));

      const validQuestions = newQuestions.filter(
        q => q.question_text && q.correct_answer && Array.isArray(q.correct_answer) && q.correct_answer.length > 0
      );

      if (validQuestions.length > 0) {
        await bulkInsertQuestions(validQuestions);
        toast.success(`Successfully imported ${validQuestions.length} questions.`);
      } else {
        toast.warning('No valid questions found in the file to upload. Please check the file format.');
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Upload failed: ${error.message}`);
      } else {
        toast.error('An unknown error occurred during upload.');
      }
    } finally {
      setUploading(false);
    }
  };

  return { 
    questions, 
    loading, 
    uploading,
    fetchQuestions, 
    addQuestion, 
    updateQuestion, 
    deleteQuestion, 
    bulkDeleteQuestions,
    uploadFile,
    bulkInsertQuestions,
    bulkUpdateQuestions,
    searchTerm,
    setSearchTerm,
    typeFilter,
    setTypeFilter,
    currentPage,
    setCurrentPage,
    totalQuestions: filteredQuestions.length,
    questionsPerPage,
    paginatedQuestions,
  };
};
